// BACKEND INTEGRATION POINT: Replace all exports with API calls to your vendor auth service,
// PO management API, and GRN submission endpoint.

export type Vendor = {
  id: string;
  name: string;
  email: string;
  password: string;
  contactPerson: string;
  gstin: string;
};

export type POLineItem = {
  id: string;
  itemCode: string;
  itemName: string;
  brand: string;
  company: string;
  unit: string;
  orderedQty: number;
  deliveredQty: number; // sum of all previously submitted GRNs
  unitPrice: number;
};

export type PurchaseOrder = {
  id: string;
  poNumber: string;
  vendorId: string;
  buyerName: string;
  buyerDept: string;
  deadlineDaysFromToday: number; // positive = future, negative = overdue
  deadlineDate: string; // ISO date string YYYY-MM-DD
  lineItems: POLineItem[];
  createdDate: string;
  totalValue: number;
};

export type SubmittedGRN = {
  id: string;
  grnRef: string;
  poId: string;
  submittedAt: string;
  challaN: string;
  lines: { lineItemId: string; batchNo: string; expiryDate: string; qty: number }[];
};

// ─── Vendors ──────────────────────────────────────────────────────────────────

export const VENDORS: Vendor[] = [
  {
    id: 'vendor-001',
    name: 'MedSupply Co.',
    email: 'warehouse@medsupply.co',
    password: 'MedSupply@2026',
    contactPerson: 'Arjun Mehta',
    gstin: '27AABCM1234A1Z5',
  },
  {
    id: 'vendor-002',
    name: 'PharmaCare Ltd.',
    email: 'dispatch@pharmacare.in',
    password: 'PharmaCare@2026',
    contactPerson: 'Priya Nair',
    gstin: '29AADCP5678B1Z3',
  },
];

// ─── Helper: compute deadline date from today + offset ────────────────────────

function deadlineDate(daysOffset: number): string {
  const d = new Date('2026-07-01');
  d.setDate(d.getDate() + daysOffset);
  return d.toISOString().split('T')[0];
}

// ─── Purchase Orders ───────────────────────────────────────────────────────────

export const PURCHASE_ORDERS: PurchaseOrder[] = [
  // MedSupply Co. POs
  {
    id: 'po-001',
    poNumber: 'PO-2026-00841',
    vendorId: 'vendor-001',
    buyerName: 'All India Institute of Medical Sciences',
    buyerDept: 'Central Stores',
    deadlineDaysFromToday: -3,
    deadlineDate: deadlineDate(-3),
    createdDate: '2026-06-01',
    totalValue: 284500,
    lineItems: [
      {
        id: 'li-001-01',
        itemCode: 'SRG-0041',
        itemName: 'Surgical Gloves (Sterile)',
        brand: 'MedGrip',
        company: 'MedSupply Co.',
        unit: 'Box (100 pcs)',
        orderedQty: 500,
        deliveredQty: 320,
        unitPrice: 285,
      },
      {
        id: 'li-001-02',
        itemCode: 'SRG-0098',
        itemName: 'Disposable Syringes 5ml',
        brand: 'SafeShot',
        company: 'MedSupply Co.',
        unit: 'Box (100 pcs)',
        orderedQty: 1000,
        deliveredQty: 600,
        unitPrice: 180,
      },
      {
        id: 'li-001-03',
        itemCode: 'DRS-0017',
        itemName: 'Sterile Wound Dressing 10x10cm',
        brand: 'HealFast',
        company: 'MedSupply Co.',
        unit: 'Pcs',
        orderedQty: 2000,
        deliveredQty: 2000,
        unitPrice: 22,
      },
    ],
  },
  {
    id: 'po-002',
    poNumber: 'PO-2026-00902',
    vendorId: 'vendor-001',
    buyerName: 'Safdarjung Hospital',
    buyerDept: 'OT Supplies',
    deadlineDaysFromToday: 2,
    deadlineDate: deadlineDate(2),
    createdDate: '2026-06-10',
    totalValue: 162000,
    lineItems: [
      {
        id: 'li-002-01',
        itemCode: 'OT-0055',
        itemName: 'Surgical Masks N95',
        brand: 'AirShield',
        company: 'MedSupply Co.',
        unit: 'Box (20 pcs)',
        orderedQty: 300,
        deliveredQty: 100,
        unitPrice: 320,
      },
      {
        id: 'li-002-02',
        itemCode: 'OT-0061',
        itemName: 'Disposable Surgical Caps',
        brand: 'CleanCap',
        company: 'MedSupply Co.',
        unit: 'Pcs',
        orderedQty: 1500,
        deliveredQty: 500,
        unitPrice: 12,
      },
      {
        id: 'li-002-03',
        itemCode: 'OT-0072',
        itemName: 'Sterile Drapes 60x90cm',
        brand: 'SurgiDrape',
        company: 'MedSupply Co.',
        unit: 'Pcs',
        orderedQty: 400,
        deliveredQty: 0,
        unitPrice: 85,
      },
    ],
  },
  {
    id: 'po-003',
    poNumber: 'PO-2026-00955',
    vendorId: 'vendor-001',
    buyerName: 'Ram Manohar Lohia Hospital',
    buyerDept: 'Pharmacy',
    deadlineDaysFromToday: 6,
    deadlineDate: deadlineDate(6),
    createdDate: '2026-06-15',
    totalValue: 98400,
    lineItems: [
      {
        id: 'li-003-01',
        itemCode: 'PHM-0033',
        itemName: 'IV Cannula 18G',
        brand: 'VeinPath',
        company: 'MedSupply Co.',
        unit: 'Pcs',
        orderedQty: 800,
        deliveredQty: 200,
        unitPrice: 48,
      },
      {
        id: 'li-003-02',
        itemCode: 'PHM-0041',
        itemName: 'Normal Saline 500ml',
        brand: 'FluidCare',
        company: 'MedSupply Co.',
        unit: 'Bag',
        orderedQty: 600,
        deliveredQty: 300,
        unitPrice: 62,
      },
    ],
  },
  {
    id: 'po-004',
    poNumber: 'PO-2026-01003',
    vendorId: 'vendor-001',
    buyerName: 'Lady Hardinge Medical College',
    buyerDept: 'General Stores',
    deadlineDaysFromToday: 10,
    deadlineDate: deadlineDate(10),
    createdDate: '2026-06-20',
    totalValue: 54600,
    lineItems: [
      {
        id: 'li-004-01',
        itemCode: 'GEN-0089',
        itemName: 'Examination Gloves (Non-Sterile)',
        brand: 'MedGrip',
        company: 'MedSupply Co.',
        unit: 'Box (100 pcs)',
        orderedQty: 200,
        deliveredQty: 0,
        unitPrice: 165,
      },
      {
        id: 'li-004-02',
        itemCode: 'GEN-0102',
        itemName: 'Adhesive Bandage Strips',
        brand: 'StickHeal',
        company: 'MedSupply Co.',
        unit: 'Box (100 pcs)',
        orderedQty: 150,
        deliveredQty: 0,
        unitPrice: 92,
      },
      {
        id: 'li-004-03',
        itemCode: 'GEN-0115',
        itemName: 'Cotton Gauze Roll 4"',
        brand: 'SoftWrap',
        company: 'MedSupply Co.',
        unit: 'Roll',
        orderedQty: 500,
        deliveredQty: 0,
        unitPrice: 38,
      },
    ],
  },

  // PharmaCare Ltd. POs
  {
    id: 'po-005',
    poNumber: 'PO-2026-00877',
    vendorId: 'vendor-002',
    buyerName: 'AIIMS Trauma Centre',
    buyerDept: 'Emergency Stores',
    deadlineDaysFromToday: -1,
    deadlineDate: deadlineDate(-1),
    createdDate: '2026-06-05',
    totalValue: 312800,
    lineItems: [
      {
        id: 'li-005-01',
        itemCode: 'EMG-0021',
        itemName: 'Adrenaline Injection 1mg/ml',
        brand: 'VitalDose',
        company: 'PharmaCare Ltd.',
        unit: 'Ampoule',
        orderedQty: 500,
        deliveredQty: 300,
        unitPrice: 85,
      },
      {
        id: 'li-005-02',
        itemCode: 'EMG-0034',
        itemName: 'Atropine Sulphate 0.6mg',
        brand: 'VitalDose',
        company: 'PharmaCare Ltd.',
        unit: 'Ampoule',
        orderedQty: 400,
        deliveredQty: 150,
        unitPrice: 72,
      },
      {
        id: 'li-005-03',
        itemCode: 'EMG-0047',
        itemName: 'Hydrocortisone 100mg Vial',
        brand: 'CortiShield',
        company: 'PharmaCare Ltd.',
        unit: 'Vial',
        orderedQty: 300,
        deliveredQty: 300,
        unitPrice: 145,
      },
    ],
  },
  {
    id: 'po-006',
    poNumber: 'PO-2026-00931',
    vendorId: 'vendor-002',
    buyerName: 'Safdarjung Hospital',
    buyerDept: 'ICU Pharmacy',
    deadlineDaysFromToday: 5,
    deadlineDate: deadlineDate(5),
    createdDate: '2026-06-18',
    totalValue: 187600,
    lineItems: [
      {
        id: 'li-006-01',
        itemCode: 'ICU-0011',
        itemName: 'Morphine Sulphate 10mg/ml',
        brand: 'PainEase',
        company: 'PharmaCare Ltd.',
        unit: 'Ampoule',
        orderedQty: 200,
        deliveredQty: 80,
        unitPrice: 220,
      },
      {
        id: 'li-006-02',
        itemCode: 'ICU-0022',
        itemName: 'Midazolam 5mg/ml',
        brand: 'SedaPharm',
        company: 'PharmaCare Ltd.',
        unit: 'Vial',
        orderedQty: 150,
        deliveredQty: 50,
        unitPrice: 310,
      },
      {
        id: 'li-006-03',
        itemCode: 'ICU-0033',
        itemName: 'Noradrenaline 4mg/4ml',
        brand: 'VasoCore',
        company: 'PharmaCare Ltd.',
        unit: 'Ampoule',
        orderedQty: 250,
        deliveredQty: 100,
        unitPrice: 195,
      },
    ],
  },
  {
    id: 'po-007',
    poNumber: 'PO-2026-01044',
    vendorId: 'vendor-002',
    buyerName: 'Lok Nayak Hospital',
    buyerDept: 'Central Pharmacy',
    deadlineDaysFromToday: 12,
    deadlineDate: deadlineDate(12),
    createdDate: '2026-06-25',
    totalValue: 74200,
    lineItems: [
      {
        id: 'li-007-01',
        itemCode: 'CPH-0044',
        itemName: 'Paracetamol 500mg Tablet',
        brand: 'FeverClear',
        company: 'PharmaCare Ltd.',
        unit: 'Strip (10 tabs)',
        orderedQty: 5000,
        deliveredQty: 0,
        unitPrice: 8,
      },
      {
        id: 'li-007-02',
        itemCode: 'CPH-0058',
        itemName: 'Amoxicillin 500mg Capsule',
        brand: 'BactiShield',
        company: 'PharmaCare Ltd.',
        unit: 'Strip (10 caps)',
        orderedQty: 2000,
        deliveredQty: 0,
        unitPrice: 22,
      },
      {
        id: 'li-007-03',
        itemCode: 'CPH-0071',
        itemName: 'Metformin 500mg Tablet',
        brand: 'GlucoGuard',
        company: 'PharmaCare Ltd.',
        unit: 'Strip (15 tabs)',
        orderedQty: 3000,
        deliveredQty: 0,
        unitPrice: 14,
      },
    ],
  },
];

// ─── In-memory GRN store (simulates backend delivery accumulation) ─────────────
// BACKEND INTEGRATION POINT: Replace with database-backed GRN storage.
// Remaining qty MUST be computed server-side to prevent race conditions.

export const SUBMITTED_GRNS: SubmittedGRN[] = [];

let grnCounter = 1;

export function generateGRNRef(): string {
  const ref = `GRN-2026-${String(grnCounter).padStart(5, '0')}`;
  grnCounter++;
  return ref;
}

export function getPOsForVendor(vendorId: string): PurchaseOrder[] {
  return PURCHASE_ORDERS.filter((po) => po.vendorId === vendorId);
}

export function getPOById(poId: string): PurchaseOrder | undefined {
  return PURCHASE_ORDERS.find((po) => po.id === poId);
}

export function getDeliveredQtyForLine(lineItemId: string): number {
  // BACKEND INTEGRATION POINT: Query SUM(qty) from grn_lines WHERE line_item_id = lineItemId
  const base = PURCHASE_ORDERS.flatMap((po) => po.lineItems).find(
    (li) => li.id === lineItemId
  )?.deliveredQty ?? 0;

  const fromGRNs = SUBMITTED_GRNS.flatMap((g) => g.lines)
    .filter((l) => l.lineItemId === lineItemId)
    .reduce((sum, l) => sum + l.qty, 0);

  return base + fromGRNs;
}

export function applyGRN(grn: Omit<SubmittedGRN, 'id' | 'grnRef' | 'submittedAt'>): SubmittedGRN {
  // BACKEND INTEGRATION POINT: INSERT INTO grns + grn_lines, return generated GRN ref
  const record: SubmittedGRN = {
    id: `grn-${Date.now()}`,
    grnRef: generateGRNRef(),
    submittedAt: new Date().toISOString(),
    ...grn,
  };
  SUBMITTED_GRNS.push(record);
  return record;
}

export function formatDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(day).padStart(2, '0')} ${months[month - 1]} ${year}`;
}

export function getUrgencyLevel(daysFromToday: number): 'overdue' | 'critical' | 'warning' | 'safe' {
  if (daysFromToday < 0) return 'overdue';
  if (daysFromToday <= 3) return 'critical';
  if (daysFromToday <= 7) return 'warning';
  return 'safe';
}

export function getPOFulfillmentPercent(po: PurchaseOrder): number {
  const totalOrdered = po.lineItems.reduce((s, li) => s + li.orderedQty, 0);
  if (totalOrdered === 0) return 0;
  const totalDelivered = po.lineItems.reduce((s, li) => s + getDeliveredQtyForLine(li.id), 0);
  return Math.min(100, Math.round((totalDelivered / totalOrdered) * 100));
}

export function getPOStatus(po: PurchaseOrder): 'Open' | 'Partial' | 'Fulfilled' | 'Overdue' {
  const pct = getPOFulfillmentPercent(po);
  if (pct === 100) return 'Fulfilled';
  if (po.deadlineDaysFromToday < 0) return 'Overdue';
  if (pct > 0) return 'Partial';
  return 'Open';
}